import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";

import {
  fetchWorkspaceLogo,
  removeWorkspaceLogo,
  selectWorkspaceLogo,
  uploadWorkspaceLogo,
} from "@/entities/workspace/brandAssetsApi";
import { Button } from "@/shared/ui/button";
import { Card } from "@/shared/ui/card";

export function BrandAssetsSection({ workspaceId }: { workspaceId: string }): JSX.Element {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["workspace-brand-assets", workspaceId],
    queryFn: () => fetchWorkspaceLogo(workspaceId),
  });
  const mutation = useMutation({
    mutationFn: (conceptId: string) => selectWorkspaceLogo(workspaceId, conceptId),
    onSuccess: (next) => {
      qc.setQueryData(["workspace-brand-assets", workspaceId], next);
      toast.success("Workspace logo updated");
    },
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : "Could not update the logo"),
  });
  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadWorkspaceLogo(workspaceId, file),
    onSuccess: (next) => {
      qc.setQueryData(["workspace-brand-assets", workspaceId], next);
      toast.success("Logo uploaded");
    },
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : "Could not upload the logo"),
  });
  const removeMutation = useMutation({
    mutationFn: () => removeWorkspaceLogo(workspaceId),
    onSuccess: (next) => {
      qc.setQueryData(["workspace-brand-assets", workspaceId], next);
      toast.success("Workspace logo removed");
    },
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : "Could not remove the logo"),
  });

  return (
    <Card className="p-6" data-testid="settings-brand-assets">
      <div className="mb-4">
        <h2 className="text-lg font-semibold">Brand assets</h2>
        <p className="text-xs text-muted-foreground">
          Choose the workspace logo without reopening your original setup.
        </p>
      </div>

      {query.isPending && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading logo concepts…
        </div>
      )}
      {query.isError && (
        <p className="text-sm text-destructive">Logo concepts could not be loaded.</p>
      )}
      {query.data && (
        <div className="space-y-6">
          <div className="rounded-xl border border-dashed p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-medium">Upload your logo</p>
                <p className="text-xs text-muted-foreground">SVG, PNG, or WebP · maximum 2 MB</p>
              </div>
              <label className="inline-flex cursor-pointer items-center justify-center rounded-md border px-4 py-2 text-sm font-medium hover:bg-accent">
                <Upload className="mr-2 h-4 w-4" />
                {uploadMutation.isPending ? "Uploading…" : "Choose file"}
                <input
                  type="file"
                  className="sr-only"
                  accept="image/svg+xml,image/png,image/webp"
                  disabled={uploadMutation.isPending}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) uploadMutation.mutate(file);
                    event.currentTarget.value = "";
                  }}
                  data-testid="settings-logo-upload"
                />
              </label>
            </div>
            {query.data.selected_kind === "upload" && query.data.uploaded_logo_url && (
              <div className="mt-4 flex items-center gap-4">
                <img
                  src={query.data.uploaded_logo_url}
                  alt="Current workspace logo"
                  className="h-20 w-20 rounded-lg bg-white object-contain p-2"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{query.data.uploaded_logo_filename ?? "Uploaded logo"}</p>
                  <p className="text-xs text-muted-foreground">Current logo</p>
                </div>
              </div>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-3" data-testid="settings-logo-concepts">
          {query.data.concepts.map((concept) => {
            const selected = query.data.selected_kind === "curated" && query.data.selected_concept_id === concept.id;
            return (
              <div
                key={concept.id}
                className={`rounded-xl border p-3 ${selected ? "border-primary" : "border-border"}`}
              >
                <div
                  className="grid aspect-square place-items-center overflow-hidden rounded-lg bg-white p-2"
                  // SVG is server-authored curated content; customer text is XML-escaped server-side.
                  dangerouslySetInnerHTML={{ __html: concept.svg }}
                />
                <p className="mt-3 text-sm font-medium">{concept.label}</p>
                <Button
                  className="mt-3 w-full"
                  size="sm"
                  variant={selected ? "secondary" : "outline"}
                  disabled={selected || mutation.isPending}
                  onClick={() => mutation.mutate(concept.id)}
                  data-testid={`settings-logo-concept-${concept.id}`}
                >
                  {selected ? <Check className="mr-2 h-4 w-4" /> : null}
                  {selected ? "Current logo" : "Use this logo"}
                </Button>
              </div>
            );
          })}
          </div>

          {query.data.selected_kind !== "none" && (
            <div className="flex justify-end">
              <Button
                variant="ghost"
                disabled={removeMutation.isPending}
                onClick={() => removeMutation.mutate()}
                data-testid="settings-logo-remove"
              >
                Use no logo
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
